<template>
  <v-container class="home-page py-6">
    <!-- ============================================================ -->
    <!-- Próximos jogos                                                -->
    <!-- ============================================================ -->
    <section class="mb-10" data-section="upcoming-matches">
      <header class="d-flex align-center mb-4">
        <v-icon color="primary" class="mr-2">mdi-calendar-clock</v-icon>
        <h2 class="text-h5 font-weight-bold text-blue-grey-darken-4">
          {{ $t("home_page.upcoming_matches") }}
        </h2>
      </header>

      <!-- Carregando: três esqueletos no lugar dos três cartões, para a home
           não saltar quando a resposta chegar. -->
      <v-row v-if="matchesLoading">
        <v-col cols="12" md="4" v-for="n in UPCOMING_LIMIT" :key="`match-skeleton-${n}`">
          <SkeletonLoader variant="card" class="skeleton-on-light" />
        </v-col>
      </v-row>

      <!-- Erro de rede: o interceptor já avisou; aqui fica um caminho de volta. -->
      <v-card v-else-if="matchesError" variant="tonal" color="error" rounded="xl" class="pa-6 text-center">
        <v-icon size="40" class="mb-2">mdi-wifi-off</v-icon>
        <p class="text-body-1 mb-3">{{ $t("home_page.load_error") }}</p>
        <v-btn variant="flat" color="error" @click="loadUpcomingMatches">{{ $t("home_page.retry") }}</v-btn>
      </v-card>

      <v-row v-else-if="upcomingMatches.length > 0">
        <v-col cols="12" md="4" v-for="match in upcomingMatches" :key="match.id">
          <v-card class="match-card h-100" rounded="xl" elevation="2">
            <v-card-text class="pa-4">
              <div class="d-flex align-center justify-space-between">
                <div class="match-card__team">
                  <v-avatar :size="56" color="grey-lighten-4" class="elevation-1">
                    <v-img
                      v-if="teamShield(match.home_team)"
                      :src="teamShield(match.home_team)"
                      :alt="teamName(match.home_team)"
                      cover
                    />
                    <v-icon v-else size="28" color="grey-darken-1">mdi-shield-outline</v-icon>
                  </v-avatar>
                  <p class="match-card__team-name text-truncate" :title="teamName(match.home_team)">
                    {{ teamName(match.home_team) }}
                  </p>
                </div>

                <div class="match-card__versus text-grey-darken-1 font-weight-bold px-2">
                  {{ $t("home_page.versus") }}
                </div>

                <div class="match-card__team">
                  <v-avatar :size="56" color="grey-lighten-4" class="elevation-1">
                    <v-img
                      v-if="teamShield(match.away_team)"
                      :src="teamShield(match.away_team)"
                      :alt="teamName(match.away_team)"
                      cover
                    />
                    <v-icon v-else size="28" color="grey-darken-1">mdi-shield-outline</v-icon>
                  </v-avatar>
                  <p class="match-card__team-name text-truncate" :title="teamName(match.away_team)">
                    {{ teamName(match.away_team) }}
                  </p>
                </div>
              </div>
            </v-card-text>

            <v-divider />

            <v-card-text class="match-card__when d-flex align-center justify-center py-3">
              <v-chip size="small" variant="tonal" color="primary" class="font-weight-bold mr-2">
                <v-icon start size="16">mdi-calendar</v-icon>
                {{ matchDateLabel(match) }}
              </v-chip>
              <!-- Partida sem horário existe: mostra "a definir" em vez de
                   inventar 00:00. -->
              <v-chip
                size="small"
                :variant="match.time ? 'tonal' : 'outlined'"
                :color="match.time ? 'primary' : 'grey-darken-1'"
                class="font-weight-bold"
              >
                <v-icon start size="16">{{ match.time ? "mdi-clock-outline" : "mdi-clock-alert-outline" }}</v-icon>
                {{ match.time || $t("home_page.time_to_be_defined") }}
              </v-chip>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- O vazio é o estado COMUM (em produção não há partida futura), então
           ele precisa ficar bom, não ser um espaço em branco. -->
      <v-card v-else class="empty-state pa-8 text-center" rounded="xl" variant="flat" data-empty="upcoming-matches">
        <v-icon size="56" color="primary" class="mb-3">mdi-calendar-blank-outline</v-icon>
        <h3 class="text-h6 font-weight-bold text-blue-grey-darken-4 mb-1">
          {{ $t("home_page.no_upcoming_matches") }}
        </h3>
        <p class="text-body-2 text-grey-darken-1 mb-0">
          {{ $t("home_page.no_upcoming_matches_hint") }}
        </p>
      </v-card>
    </section>

    <!-- ============================================================ -->
    <!-- Campeonatos                                                   -->
    <!-- ============================================================ -->
    <section data-section="championships">
      <header class="d-flex align-center flex-wrap mb-4">
        <v-icon color="primary" class="mr-2">mdi-trophy</v-icon>
        <h2 class="text-h5 font-weight-bold text-blue-grey-darken-4 mr-auto">
          {{ $t("championships") }}
        </h2>
        <v-btn variant="text" color="primary" to="/campeonatos" class="text-none">
          {{ $t("home_page.see_all") }}
          <v-icon end size="small">mdi-arrow-right</v-icon>
        </v-btn>
        <v-btn variant="flat" color="primary" to="/campeonatos/novo" class="text-none ml-2" prepend-icon="mdi-plus">
          {{ $t("home_page.create_championship") }}
        </v-btn>
      </header>

      <v-row v-if="championshipsLoading">
        <v-col cols="12" sm="6" md="4" v-for="n in CHAMPIONSHIPS_LIMIT" :key="`champ-skeleton-${n}`">
          <SkeletonLoader variant="championship" />
        </v-col>
      </v-row>

      <v-card v-else-if="championshipsError" variant="tonal" color="error" rounded="xl" class="pa-6 text-center">
        <v-icon size="40" class="mb-2">mdi-wifi-off</v-icon>
        <p class="text-body-1 mb-3">{{ $t("home_page.load_error") }}</p>
        <v-btn variant="flat" color="error" @click="loadChampionships">{{ $t("home_page.retry") }}</v-btn>
      </v-card>

      <v-row v-else-if="championships.length > 0">
        <v-col cols="12" sm="6" md="4" v-for="championship in championships" :key="championship.id">
          <CardFigurinha
            class="h-100"
            :title="championship.attributes.name"
            :subtitle="championshipSubtitle(championship)"
            :image="championshipLogo(championship)"
            icon="mdi-trophy"
            :badge="stateLabel(championship)"
            :badge-type="stateBadgeType(championship)"
            clickable
            @click="openChampionship(championship)"
          >
            <!-- O campeão é faixa de destaque, não estado: pode conviver com
                 o selo "em andamento" (acontece na Copa Alê Importados). -->
            <div v-if="championOf(championship)" class="champion-strip d-flex align-center mt-3 px-2 py-1">
              <v-icon color="amber-darken-2" size="18" class="mr-2">mdi-trophy</v-icon>
              <v-avatar size="24" class="mr-2 bg-white">
                <v-img
                  v-if="championOf(championship).escudo"
                  :src="championOf(championship).escudo"
                  :alt="championOf(championship).nome"
                  cover
                />
                <v-icon v-else size="14" color="grey-darken-1">mdi-shield-outline</v-icon>
              </v-avatar>
              <div class="text-left text-truncate">
                <div class="text-caption text-amber-darken-3 lh-1">{{ $t("campeonato.campeao") }}</div>
                <div class="text-body-2 font-weight-bold text-truncate" :title="championOf(championship).nome">
                  {{ championOf(championship).nome }}
                </div>
              </div>
            </div>
          </CardFigurinha>
        </v-col>
      </v-row>

      <v-card v-else class="empty-state pa-8 text-center" rounded="xl" variant="flat" data-empty="championships">
        <v-icon size="56" color="primary" class="mb-3">mdi-trophy-outline</v-icon>
        <h3 class="text-h6 font-weight-bold text-blue-grey-darken-4 mb-1">
          {{ $t("home_page.no_championships") }}
        </h3>
        <p class="text-body-2 text-grey-darken-1 mb-4">
          {{ $t("home_page.no_championships_hint") }}
        </p>
        <v-btn variant="flat" color="primary" to="/campeonatos/novo" class="text-none" prepend-icon="mdi-plus">
          {{ $t("home_page.create_championship") }}
        </v-btn>
      </v-card>
    </section>
  </v-container>
</template>

<script>
import Api from "@/api/index";
import CardFigurinha from "@/components/shared/CardFigurinha.vue";
import SkeletonLoader from "@/components/shared/SkeletonLoader.vue";
import { escudo, logoDeCampeonato, campeao } from "@/utils/imagens";

// Quantos itens a home mostra. O número é da TELA — a API aceita `limit`
// justamente para não cravá-lo no servidor.
const UPCOMING_LIMIT = 3;
const CHAMPIONSHIPS_LIMIT = 6;

// Selo por estado. O campo `progress_state` vem PRONTO do servidor (a regra
// mora em `Championship#progress_state`, na API); aqui só se escolhe a cor.
// `status_id` não entra: é nulo em todos os campeonatos e significa exclusão
// lógica, não andamento.
const STATE_BADGE_TYPE = {
  ongoing: "success",
  finished: "gold",
  upcoming: "primary"
};

// Ordem de exibição na home: o que está acontecendo primeiro, depois o que
// vem, por fim o que acabou. Em produção 9 dos 12 campeonatos estão
// finalizados — sem isto, os 6 cartões da home seriam todos "finalizado" e os
// 3 em andamento (o que interessa a quem abre o app) ficariam de fora.
// É só ordenação de apresentação; o estado continua vindo do servidor.
const STATE_DISPLAY_ORDER = { ongoing: 0, upcoming: 1, finished: 2 };

export default {
  name: "HomePage",
  components: { CardFigurinha, SkeletonLoader },
  data() {
    return {
      UPCOMING_LIMIT,
      CHAMPIONSHIPS_LIMIT,
      upcomingMatches: [],
      matchesLoading: true,
      matchesError: false,
      championships: [],
      championshipsLoading: true,
      championshipsError: false
    };
  },
  created() {
    this.loadUpcomingMatches();
    this.loadChampionships();
  },
  methods: {
    async loadUpcomingMatches() {
      this.matchesLoading = true;
      this.matchesError = false;
      try {
        const response = await Api.Match.upcoming({ limit: UPCOMING_LIMIT });
        this.upcomingMatches = this.dataOf(response).map(item => ({ id: item.id, ...item.attributes }));
      } catch (e) {
        // O interceptor do axios já mostrou o erro; aqui só se troca a lista
        // por um botão de tentar de novo.
        this.upcomingMatches = [];
        this.matchesError = true;
      } finally {
        this.matchesLoading = false;
      }
    },
    async loadChampionships() {
      this.championshipsLoading = true;
      this.championshipsError = false;
      try {
        const response = await Api.Championship.index();
        this.championships = this.dataOf(response)
          .filter(item => item?.attributes)
          .map((item, index) => ({ item, index }))
          // `sort` é estável no ES2019+, mas o índice garante a ordem da API
          // como desempate mesmo onde não for.
          .sort((a, b) => this.stateOrder(a.item) - this.stateOrder(b.item) || a.index - b.index)
          .map(({ item }) => item)
          .slice(0, CHAMPIONSHIPS_LIMIT);
      } catch (e) {
        this.championships = [];
        this.championshipsError = true;
      } finally {
        this.championshipsLoading = false;
      }
    },
    // A API responde JSON:API (`{ data: [...] }`); o axios embrulha em `data`.
    dataOf(response) {
      const lista = response?.data?.data;
      return Array.isArray(lista) ? lista : [];
    },

    // ---------------------------------------------------------- partidas
    teamName(champTeam) {
      return champTeam?.data?.attributes?.team_name || "";
    },
    // `escudo()` prefere a variante pequena e degrada até o original: a API de
    // produção não tem variante nenhuma.
    teamShield(champTeam) {
      return escudo(champTeam?.data?.attributes);
    },
    // "Hoje" e "Amanhã" em vez da data, quando for o caso. Compara a data ISO
    // (`match_date`, AAAA-MM-DD) com a data LOCAL do navegador — `toISOString`
    // usaria UTC e às 22h de Brasília já diria que hoje é amanhã.
    matchDateLabel(match) {
      const matchDate = match.match_date;
      if (!matchDate) return match.date3 || "";
      if (matchDate === this.localIsoDate(0)) return this.$t("home_page.today");
      if (matchDate === this.localIsoDate(1)) return this.$t("home_page.tomorrow");
      return match.date3 || matchDate;
    },
    localIsoDate(daysFromToday) {
      const d = new Date();
      d.setDate(d.getDate() + daysFromToday);
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${d.getFullYear()}-${mm}-${dd}`;
    },

    // ------------------------------------------------------- campeonatos
    championshipLogo(championship) {
      // `thumb` porque o CardFigurinha desenha o escudo em 80 px; a cadeia
      // thumb -> card -> logo garante que ele não some em campeonato antigo.
      return logoDeCampeonato(championship.attributes, "thumb");
    },
    championshipSubtitle(championship) {
      const attrs = championship.attributes;
      const modality = attrs.edition_modality?.data?.[0]?.attributes?.modality_name;
      const place = attrs.city && attrs.state ? `${attrs.city} - ${attrs.state}` : attrs.city || attrs.state;
      return [modality, place].filter(Boolean).join(" • ");
    },
    // Sem `progress_state` (API antiga, sem o campo) não há selo — não se
    // deriva o estado no cliente.
    stateLabel(championship) {
      const state = championship.attributes.progress_state;
      return STATE_BADGE_TYPE[state] ? this.$t(`championship_state.${state}`) : undefined;
    },
    // Sem `progress_state` (API antiga) vai para o fim, atrás dos finalizados.
    stateOrder(championship) {
      const order = STATE_DISPLAY_ORDER[championship.attributes.progress_state];
      return order === undefined ? 3 : order;
    },
    stateBadgeType(championship) {
      return STATE_BADGE_TYPE[championship.attributes.progress_state] || "primary";
    },
    championOf(championship) {
      return campeao(championship.attributes);
    },
    // Mesmo destino da lista de campeonatos: a aba de jogos da última
    // modalidade.
    openChampionship(championship) {
      const ids = championship.attributes.edition_modality_ids || [];
      const id = ids.length > 0 ? ids[ids.length - 1] : championship.id;
      this.$router.push(`/campeonatos/${id}/jogos`);
    }
  }
};
</script>

<style scoped>
.match-card {
  background: #fff;
}

.match-card__team {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.match-card__team-name {
  margin: 8px 0 0;
  max-width: 100%;
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
}

.match-card__versus {
  font-size: 13px;
  letter-spacing: 1px;
  text-transform: uppercase;
}

/* O SkeletonLoader foi desenhado para fundo escuro (tons de branco quase
   transparentes); sobre o cinza-claro da home ele sumiria. Um fundo escuro
   próprio, do mesmo tom do esqueleto de campeonato, devolve o contraste. */
.skeleton-on-light {
  background: linear-gradient(145deg, #1a1a2e 0%, #16213e 100%);
  border-radius: 16px;
}

.empty-state {
  background: #fff;
  border: 1px dashed rgba(0, 58, 62, 0.35);
}

.champion-strip {
  width: 100%;
  background: rgba(255, 193, 7, 0.15);
  border-left: 3px solid #ffc107;
  border-radius: 6px;
  min-width: 0;
}
.champion-strip .text-truncate {
  min-width: 0;
}
.lh-1 {
  line-height: 1.1;
}
</style>
